import React, { useState, useEffect, useCallback } from 'react';
import {
  MdInfo,
  MdPerson,
  MdRefresh,
  MdSearch,
  MdClose,
  MdLock,
  MdBarChart,
  MdArrowUpward,
  MdArrowDownward,
  MdSwapVert,
  MdCheck,
  MdWarning,
  MdClose as MdCross,
  MdSchool,
  MdSupervisorAccount,
  MdAssistant,
  MdFeedback,
  MdVerifiedUser,
  MdPeople,
  MdPersonAdd,
  MdPersonRemove,
  MdCloudUpload
} from 'react-icons/md';
import { canViewUserInfo, canEnrollStudents } from '../../utils/permissions';
import { useClass } from '../../utils/ClassContext';

const UsersInfo = ({
  username,
  userRole,
  userName,
  apiBaseUrl,
  onNavigate
}) => {
  const { selectedClass } = useClass();
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Enrollment management state
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Check if user has permission to view user info
  const hasPermission = canViewUserInfo(userRole);
  const canEnroll = canEnrollStudents(userRole);

  const fetchUsersData = useCallback(async () => {
    if (!selectedClass) {
      setUsersData([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch users enrolled in the selected class
      const response = await fetch(`${apiBaseUrl}/classes/${selectedClass.class_id}/enrollments`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Use enrollment data which now includes consent_status and feedback_count
          setUsersData(data.enrollments || []);
        } else {
          setError(data.error || 'Failed to fetch user data');
        }
      } else {
        const errorText = await response.text();
        console.error('API Error:', response.status, errorText);
        setError(`Failed to fetch user data: ${response.status} ${response.statusText}`);
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, userRole, username, selectedClass]);

  // Fetch all available users for enrollment
  const fetchAvailableUsers = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/users-list`, {
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableUsers(data.users || []);
      } else {
        throw new Error('Failed to fetch users');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Enroll users in class
  const enrollUsers = async () => {
    try {
      setActionLoading(true);
      const response = await fetch(`${apiBaseUrl}/classes/bulk-enroll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        },
        body: JSON.stringify({ 
          class_id: selectedClass.class_id, 
          usernames: selectedUsers 
        })
      });

      if (response.ok) {
        // Refresh the enrolled users list immediately
        await fetchUsersData();
        // Refresh available users list to remove newly enrolled users
        await fetchAvailableUsers();
        // Clear selected users after successful enrollment
        setSelectedUsers([]);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to enroll users');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Unenroll user from class
  const unenrollUser = async (usernameToRemove) => {
    if (!window.confirm(`Are you sure you want to remove ${usernameToRemove} from this class?`)) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await fetch(`${apiBaseUrl}/classes/${selectedClass.class_id}/unenroll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        },
        body: JSON.stringify({ username: usernameToRemove })
      });

      if (response.ok) {
        // Refresh the enrolled users list immediately
        await fetchUsersData();
        // Refresh available users list if modal is open
        if (showEnrollmentModal) {
          await fetchAvailableUsers();
        }
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to unenroll user');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle CSV upload
  const handleCsvUpload = async () => {
    if (!csvFile) {
      setError('Please select a CSV file');
      return;
    }

    const formData = new FormData();
    formData.append('file', csvFile);
    formData.append('class_id', selectedClass.class_id);

    try {
      setActionLoading(true);
      const response = await fetch(`${apiBaseUrl}/classes/bulk-enroll-csv`, {
        method: 'POST',
        headers: {
          'User-Role': userRole,
          'Requesting-User': username
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        // Refresh the enrolled users list immediately
        await fetchUsersData();
        // Refresh available users list
        if (showEnrollmentModal) {
          await fetchAvailableUsers();
        }
        setShowCsvUpload(false);
        setCsvFile(null);
        
        // Show success message with details
        if (result.summary) {
          alert(`CSV Upload Complete:\n${result.summary.users_created} users created\n${result.summary.successful_enrollments} users enrolled\n${result.summary.failed_enrollments} failed`);
        }
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to upload CSV');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Get available users for enrollment (not already enrolled)
  const getAvailableUsersForEnrollment = () => {
    const enrolledUsernames = usersData.map(user => user.username).filter(Boolean);
    return availableUsers.filter(user => user.username && !enrolledUsernames.includes(user.username));
  };

  // Handle manage enrollments
  const handleManageEnrollments = async () => {
    await fetchAvailableUsers();
    setShowEnrollmentModal(true);
  };

  useEffect(() => {
    if (!hasPermission) {
      setLoading(false);
      return;
    }

    // Only fetch if we have valid userRole and username
    if (userRole && username) {
      fetchUsersData();
    } else {
      setLoading(false);
    }
  }, [hasPermission, apiBaseUrl, fetchUsersData, userRole, username]);

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort users data based on current sort config
  const sortedUsersData = React.useMemo(() => {
    if (!sortConfig.key) return usersData;

    return [...usersData].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';

      if (sortConfig.direction === 'asc') {
        return aValue.toString().localeCompare(bValue.toString());
      } else {
        return bValue.toString().localeCompare(aValue.toString());
      }
    });
  }, [usersData, sortConfig]);

  // Filter users data based on search term
  const filteredUsersData = React.useMemo(() => {
    if (!searchTerm) return sortedUsersData;

    return sortedUsersData.filter(user =>
      Object.values(user).some(value =>
        value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [sortedUsersData, searchTerm]);

  // Get sort indicator
  const getSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <MdSwapVert className="text-gray-400 text-lg" />;
    }
    return sortConfig.direction === 'asc' ?
      <MdArrowUpward className="text-blue-600 text-lg" /> :
      <MdArrowDownward className="text-blue-600 text-lg" />;
  };

  // Get role icon
  const getRoleIcon = (role) => {
    switch (role) {
      case 'Instructor':
        return <MdSupervisorAccount className="text-purple-600 text-lg" />;
      case 'TA':
        return <MdAssistant className="text-green-600 text-lg" />;
      case 'Student':
        return <MdSchool className="text-blue-600 text-lg" />;
      default:
        return <MdPerson className="text-gray-600 text-lg" />;
    }
  };

  // Get role badge color
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Instructor':
        return 'bg-purple-100 text-purple-800';
      case 'TA':
        return 'bg-green-100 text-green-800';
      case 'Student':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Calculate statistics
  const stats = React.useMemo(() => {
    const totalUsers = usersData.length;
    const roles = usersData.reduce((acc, user) => {
      acc[user.role_name] = (acc[user.role_name] || 0) + 1;
      return acc;
    }, {});
    const consentedUsers = usersData.filter(user => user.consent_status > 0).length;
    const activeUsers = usersData.filter(user => user.feedback_count > 0).length;

    return {
      totalUsers,
      roles,
      consentedUsers,
      activeUsers
    };
  }, [usersData]);

  // Render access denied message
  if (!hasPermission) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center py-8">
            <MdLock className="text-6xl text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">
              You need instructor privileges to view user information.
            </p>
            <button
              onClick={() => onNavigate('home')}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md transition-colors duration-200"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Users Information</h1>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading users data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Users Information</h1>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <MdWarning className="text-2xl text-red-600 mr-3" />
              <div>
                <h3 className="text-lg font-medium text-red-800">Error Loading Data</h3>
                <p className="text-red-700">{error}</p>
              </div>
            </div>
            <button
              onClick={fetchUsersData}
              className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Users Information</h1>
            <p className="text-gray-600">
              {selectedClass ? 
                `Students enrolled in ${selectedClass.class_name} (${selectedClass.class_code})` : 
                'No class selected'
              }
            </p>
          </div>
          <div className="flex items-center gap-3">
            {canEnroll && selectedClass && (
              <button
                onClick={handleManageEnrollments}
                className="bg-gradient-to-br from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200"
                title="Manage Enrollments"
              >
                <MdPeople />
                Manage Enrollments
              </button>
            )}
            <div className="hidden md:block">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                <MdInfo className="text-white text-2xl" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <MdPerson className="text-blue-600 text-2xl" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-800">Total Users</h3>
              <p className="text-2xl font-bold text-blue-600">{stats.totalUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <MdVerifiedUser className="text-green-600 text-2xl" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-800">Consented</h3>
              <p className="text-2xl font-bold text-green-600">{stats.consentedUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <MdFeedback className="text-purple-600 text-2xl" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-800">Active Users</h3>
              <p className="text-2xl font-bold text-purple-600">{stats.activeUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <MdBarChart className="text-orange-600 text-2xl" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-800">Roles</h3>
              <div className="text-sm text-gray-600">
                {Object.entries(stats.roles).map(([role, count]) => (
                  <div key={role}>{role}: {count}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Controls Card */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <MdBarChart className="text-blue-600 text-2xl" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-800">
                Total Users: {usersData.length}
              </h3>
              <p className="text-sm text-gray-600">
                {filteredUsersData.length} shown after filtering
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center w-full sm:w-auto">
            <div className="flex-1 sm:flex-none sm:w-80">
              <label htmlFor="search" className="sr-only">Search users</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MdSearch className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="search"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 placeholder-gray-400"
                />
                {searchTerm && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      onClick={() => setSearchTerm('')}
                      className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                      title="Clear search"
                    >
                      <MdClose className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={fetchUsersData}
                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                title="Refresh users data"
              >
                <MdRefresh className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Users Data Table */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">User Directory</h2>
          <p className="text-sm text-gray-600 mt-1">
            Comprehensive information about all system users
          </p>
        </div>

        {filteredUsersData.length === 0 ? (
          <div className="text-center py-12">
            <MdInfo className="text-6xl text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 mb-2">
              {usersData.length === 0 ? 'No Users Data Found' : 'No Results Found'}
            </h3>
            <p className="text-gray-600">
              {usersData.length === 0
                ? 'No user data is available.'
                : 'Try adjusting your search criteria.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                    onClick={() => handleSort('username')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Username</span>
                      {getSortIndicator('username')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                    onClick={() => handleSort('firstname')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>First Name</span>
                      {getSortIndicator('firstname')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                    onClick={() => handleSort('lastname')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Last Name</span>
                      {getSortIndicator('lastname')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                    onClick={() => handleSort('role_name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Role</span>
                      {getSortIndicator('role_name')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                    onClick={() => handleSort('consent_status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Consent</span>
                      {getSortIndicator('consent_status')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                    onClick={() => handleSort('feedback_count')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Feedback Count</span>
                      {getSortIndicator('feedback_count')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsersData.map((user, index) => (
                  <tr
                    key={user.username || index}
                    className="hover:bg-gray-50 transition-colors duration-200"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <MdPerson className="text-blue-600 text-sm" />
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          {user.username || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {user.firstname || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {user.lastname || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getRoleIcon(user.role_name)}
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role_name)}`}>
                          {user.role_name || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {user.consent_status > 0 ? (
                          <>
                            <MdCheck className="text-green-500 text-lg mr-2" />
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Yes
                            </span>
                          </>
                        ) : (
                          <>
                            <MdCross className="text-red-500 text-lg mr-2" />
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              No
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <MdFeedback className="text-blue-500 text-lg mr-2" />
                        <span className="text-sm font-medium text-gray-900">
                          {user.feedback_count || 0}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer Stats */}
      {filteredUsersData.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center text-sm text-gray-600">
            Showing {filteredUsersData.length} of {usersData.length} users
          </div>
        </div>
      )}

      {/* Enrollment Management Modal */}
      {showEnrollmentModal && selectedClass && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold">Manage Enrollments</h2>
                  <p className="text-gray-600">{selectedClass.class_name} ({selectedClass.class_code})</p>
                </div>
                <button
                  onClick={() => {
                    setShowEnrollmentModal(false);
                    setSelectedUsers([]);
                    // Refresh data when modal closes to ensure we have the latest
                    fetchUsersData();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <MdClose />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Current Enrollments */}
                <div>
                  <h3 className="text-lg font-medium mb-4">Current Enrollments ({usersData.length})</h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {usersData.map((user, index) => (
                      <div key={`${user.username}-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-medium text-sm">
                              {user.firstname?.[0] || user.username?.[0]?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {user.firstname || 'Unknown'} {user.lastname || 'User'}
                            </p>
                            <p className="text-sm text-gray-600">{user.username} - {user.role_name || 'Unknown Role'}</p>
                          </div>
                        </div>
                        {canEnroll && (
                          <button
                            onClick={() => unenrollUser(user.username)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Remove from class"
                          >
                            <MdPersonRemove />
                          </button>
                        )}
                      </div>
                    ))}
                    {usersData.length === 0 && (
                      <p className="text-gray-500 text-center py-8">No students enrolled yet</p>
                    )}
                  </div>
                </div>

                {/* Add Users */}
                {canEnroll && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium">Add Users</h3>
                      <button
                        onClick={() => setShowCsvUpload(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-lg flex items-center gap-2 text-sm transition-colors"
                      >
                        <MdCloudUpload />
                        CSV Upload
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div className="max-h-96 overflow-y-auto border rounded-lg">
                        {getAvailableUsersForEnrollment().map((user, index) => (
                          <label key={`${user.username}-${index}`} className="flex items-center p-3 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user.username)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedUsers([...selectedUsers, user.username]);
                                } else {
                                  setSelectedUsers(selectedUsers.filter(u => u !== user.username));
                                }
                              }}
                              className="mr-3"
                            />
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                <span className="text-green-600 font-medium text-sm">
                                  {user.firstname?.[0] || user.username?.[0]?.toUpperCase() || '?'}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">
                                  {user.firstname} {user.lastname}
                                </p>
                                <p className="text-sm text-gray-600">{user.username} - {user.role_name}</p>
                              </div>
                            </div>
                          </label>
                        ))}
                        {getAvailableUsersForEnrollment().length === 0 && (
                          <p className="text-gray-500 text-center py-8">No users available for enrollment</p>
                        )}
                      </div>

                      {selectedUsers.length > 0 && (
                        <button
                          onClick={enrollUsers}
                          disabled={actionLoading}
                          className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {actionLoading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            <MdPersonAdd />
                          )}
                          Enroll {selectedUsers.length} Student{selectedUsers.length > 1 ? 's' : ''}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Upload Modal */}
      {showCsvUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[10000]">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Upload CSV File</h2>
                <button
                  onClick={() => {
                    setShowCsvUpload(false);
                    setCsvFile(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <MdClose />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-2">
                    Upload a CSV file with user data to automatically create users and enroll them in this class.
                  </p>
                  <p className="text-sm text-gray-600 mb-4">
                    <strong>CSV Format:</strong> username,firstname,lastname,role_id
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    Role IDs: 1=Student, 2=TA, 3=Instructor, 4=Admin
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select CSV File
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {csvFile && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <strong>Selected file:</strong> {csvFile.name}
                    </p>
                    <p className="text-sm text-blue-600">
                      Users will be created with temporary password: <strong>12345</strong>
                    </p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCsvUpload(false);
                      setCsvFile(null);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCsvUpload}
                    disabled={!csvFile || actionLoading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {actionLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <MdCloudUpload />
                    )}
                    Upload & Enroll
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersInfo;